/**
 * Telegram promo card — funnel step between "sees 2 free predictions" and
 * "start 14-day VIP trial". Invites users into the FREE Telegram channel for
 * the daily banker + results proof, which warms them up before the paid trial.
 *
 * Dismissible (hidden for 3 days), reappears so non-joiners get another nudge.
 */
import { useState } from 'react';
import { Send, ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { trackEvent } from '../utils/analytics';

const TELEGRAM_URL = 'https://t.me/+OL_GoJpb0OkyM2Jk';
const DISMISS_KEY = 'sk_scores_telegram_dismissed_at';
const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;

const ScoresTelegramCard = () => {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() - t < THREE_DAYS;
  });

  if (dismissed) return null;

  const handleDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleJoin = () => {
    try { localStorage.setItem('sk_scores_telegram_joined_click', String(Date.now())); } catch {}
    trackEvent('telegram_click', { source: 'scores_telegram_card' });
    window.open(TELEGRAM_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="mx-3 mt-3 mb-2 rounded-2xl border border-[#229ED9]/40 bg-gradient-to-br from-[#229ED9]/12 via-[#229ED9]/5 to-transparent overflow-hidden"
      data-testid="scores-telegram-card"
    >
      <div className="flex items-start gap-2.5 px-3.5 pt-3.5 pb-2">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-[#229ED9] flex items-center justify-center shadow-[0_0_16px_rgba(34,158,217,0.5)]">
          <Send className="w-4 h-4 text-white -ml-0.5" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black tracking-[0.16em] uppercase text-[#4db8e8] leading-tight">
            Free Telegram · Daily Banker
          </div>
          <div className="text-[14px] font-black text-white mt-0.5 leading-snug">
            Get our Banker of the Day — free
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 w-7 h-7 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-500"
          aria-label="Dismiss Telegram card"
          data-testid="scores-telegram-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="px-3.5 pb-3">
        <div className="space-y-1.5 mb-3">
          {['One free banker tip every day', 'Real results & proof posted daily', 'No payment — join in one tap'].map((t) => (
            <div key={t} className="flex items-center gap-2 text-[12px] text-zinc-300">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#4db8e8] shrink-0" />
              <span>{t}</span>
            </div>
          ))}
        </div>
        <button
          onClick={handleJoin}
          className="w-full px-4 py-3 rounded-xl bg-[#229ED9] hover:bg-[#1b8dc4] text-white text-[13px] font-extrabold flex items-center justify-center gap-1.5 transition-colors"
          data-testid="scores-telegram-join-cta"
        >
          <Send className="w-4 h-4 -ml-0.5" /> Join the free Telegram
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ScoresTelegramCard;
